import { useMutation } from '@apollo/client';
import { useUserContext } from '../aem-core-components/context/UserContext';
import { useFilterState } from '../components/cap';
import { useCartState } from '../contexts/cart';
import useConsumablesWithCache from './useConsumablesWithCache';
import useCartEstimate from '../components/App/hooks/useCartEstimate';
import useCheckLocationEmpty from './useCheckLocationEmpty';
import { useCheckUser } from './useCheckUser';
import { updateConsumablesAction } from '../aem-core-components/actions/consumables';
import isObjectEmpty from '../aem-core-components/utils/isObjectEmpty';
import isObjectWithKeys from '../aem-core-components/utils/isObjectWithKeys';
import { getRentablesPrice } from '../components/checkoutv2/checkoutAndOrderSummary/api/getCheckoutAPIs';
import { formatjobsiteZip, generateSKU, extractCatAndClass } from '../components/global/utils/commonUtils';
import { removeZeroPriceItems, getAvailableConsumablesConfig } from '../components/cart/utils/helper';
import { isValidString, logError } from '../components/global/utils/logger';
import { getTotalFromConsumables } from '../components/checkoutv2/utils/checkoutUtils';
import { FORCE_ITEM } from '../components/checkoutv2/constants';
import { STOCK_CLASS_FUEL, STOCK_CLASS_MISCELLANEOUS } from '../components/molecules/orderSummaryFactory/constants/orderSummaryFactoryConstants';
import { USER_TYPE } from '../constants/userDetailsConstants';
import {
    SET_CONSUMABLES,
    SET_CONSUMABLES_SELECTED_ITEMS,
    SET_IS_CART_LOADING,
    SET_SAVED_AVAILABLE_QUANTITY,
    UPDATE_CONSUMABLES
} from '../aem-core-components/actions/constants';
import MUTATION_UPDATE_CONSUMABLES from '../aem-core-components/queries/mutation_update_consumables.graphql';
import MUTATION_BULK_UPDATE_CONSUMABLES from '../aem-core-components/queries/mutation_bulk_update_consumables.graphql';
import { STORAGE_CONFIG } from '../constants/storageConfig';
import { SUCCESS } from '../components/global/constants';

const useConsumables = () => {
    const [updateConsumablesGQL] = useMutation(MUTATION_UPDATE_CONSUMABLES);
    const [bulkUpdateConsumablesGQL] = useMutation(MUTATION_BULK_UPDATE_CONSUMABLES);
    const [{ getStartDate, getEndDate }] = useCartEstimate();
    const [{ userProfile }] = useUserContext();
    const [{ cartId, cart, isCreditNewAddress, userAccount, howToGetYourOrderDetails, consumables }, dispatch] = useCartState();
    const [{ viewCart, projectDetails, startDate, endDate }] = useFilterState();
    const userType = useCheckUser();
    const { isRentalDetailsAvailable } = useCheckLocationEmpty();
    const { fetchConsumablesWithCache } = useConsumablesWithCache();

    const { selectedStoreDetails } = howToGetYourOrderDetails;
    const handlePayloadPC = pc => {
        const overridePC = JSON.parse(sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.OVERRIDEPC));
        if (overridePC?.pc) {
            return overridePC?.pc;
        } else if (pc) {
            return pc;
        } else {
            return viewCart?.pc;
        }
    };
    const handlePayloadAccount = () => {
        if (userType === USER_TYPE.CREDIT && !isCreditNewAddress) {
            return userAccount?.accountNumber;
        } else {
            return '';
        }
    };
    const handlePayloadJobNumber = () => {
        if (userType === USER_TYPE.CREDIT && !isCreditNewAddress) {
            return projectDetails?.selectedProjectJobId;
        } else {
            return '';
        }
    };

    const setRentablesPrice = async (items, pc) => {
        try {
            let zipCode = viewCart?.jobSiteZip || projectDetails?.selectedProjectZip || '';

            const drivingDistance = JSON.parse(
                sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.SELECTEDSTOREDETAILS_FOR_ROUND_TRIP) || '{}'
            )?.drivingDistanceFromJobsite;

            const browseLocationOBJ = JSON.parse(
                sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.LOCATIONPCOBJ) || '{}'
            )?.distance;
            let payload = {
                cartId: String(cart?.id || cartId) || '',
                pc: handlePayloadPC(pc),
                jobSiteAddr1: viewCart?.location || projectDetails?.projectAddress1 || '',
                jobSiteAddr2: viewCart?.jobSiteAddr2 || projectDetails?.projectAddress2 || '',
                jobSiteCity: viewCart?.jobSiteCity || projectDetails?.selectedProjectCity || '',
                jobSiteState: viewCart?.jobSiteState || projectDetails?.selectedProjectState || '',
                jobSiteZip: formatjobsiteZip(zipCode),
                dlSt: userType === USER_TYPE.CASH ? userProfile?.dlState : '',
                dlNumber: userType === USER_TYPE.CASH ? userProfile?.dlNumber : '',
                startDateAndTime: startDate ? getStartDate() : '',
                endDateAndTime: endDate ? getEndDate() : '',
                lat: String(viewCart?.lat) || String(projectDetails?.selectedProjectLatititude) || '',
                long: String(viewCart?.long) || String(projectDetails?.selectedProjectLongitude) || '',
                distance: viewCart?.isInStorePickup ? 0 : drivingDistance || browseLocationOBJ || -1,
                timeType: viewCart?.isInStorePickup ? 'Pickup' : 'Delivery',
                lastResort: selectedStoreDetails?.isLastResortBranch ? true : false,
                fulfillment: selectedStoreDetails?.fulfillmentPercent || '',
                pcLat: String(viewCart?.pcLat) || '',
                pcLong: String(viewCart?.pcLong) || '',
                projectId: String(projectDetails?.selectedProjectJobId) || '',
                isDeliveryEstimate: viewCart?.isInStorePickup ? false : true
            };
            let payloadCredit = {
                ...payload,
                account: userAccount?.accountNumber
            };
            let rentablesWithPriceMap = {};
            if (items?.length > 0) {
                let payloadWithRentables =
                    userType === USER_TYPE.CREDIT
                        ? { ...payloadCredit, equipment: [], salesEquipments: [] }
                        : { ...payload, equipment: [], salesEquipments: [] };
                for (let i in items) {
                    for (let j in items[i].rentables) {
                        payloadWithRentables = {
                            ...payloadWithRentables,
                            equipment: [
                                ...payloadWithRentables.equipment,
                                {
                                    catId: ('000' + items[i].rentables[j].rentalCat).slice(-3),
                                    classId: ('0000' + items[i].rentables[j].rentalClass).slice(-4),
                                    quantity: 1
                                }
                            ]
                        };
                    }

                    items[i].consumables.forEach(consumable => {
                        consumable?.specGroups.forEach(specGroup => {
                            specGroup?.items
                            .forEach(item => {
                                if (item?.forceItem === FORCE_ITEM.YES) {
                                    payloadWithRentables = {
                                        ...payloadWithRentables,
                                        salesEquipments: [
                                            ...payloadWithRentables.salesEquipments,
                                            {
                                                itemNumber: item?.itemNumber,
                                                quantity: parseInt(item?.minQuantity),
                                                unitPrice: parseFloat(item?.sellingPrice),
                                                stockClass: item?.stockClass,
                                                sequence: item?.sequence
                                            }
                                        ]
                                    };
                                }
                            });
                        });
                    });
                }

                if (payloadWithRentables?.equipment?.length > 0) {
                    const { data } = await getRentablesPrice(payloadWithRentables);
                    if (data) {
                        for (let k in data?.estimate?.itemizedCharges?.products) {
                            rentablesWithPriceMap[
                                generateSKU(
                                    data.estimate.itemizedCharges.products[k].catId,
                                    data.estimate.itemizedCharges.products[k].classId
                                )
                            ] = data?.estimate?.itemizedCharges?.products[k]?.unitRentalCost;
                        }
                    }
                    data?.estimate?.itemizedCharges?.salesItems.forEach(item => {
                        const key = item?.itemNumber + item?.stockClass;
                        rentablesWithPriceMap[key] = item?.unitPrice;
                    });
                }
            }
            return rentablesWithPriceMap;
        } catch (error) {
            logError(error, false, 'setRentablesPrice');
        }
    };

    const excludeForcedFuelItems = (response) => {
        const forcedFuelItems = [];
        const validFuelForcedItems = [];
        const updatedResponse = response?.map((suggestedItem) => ({
            ...suggestedItem,
            rentables: suggestedItem?.rentables,
            consumables: suggestedItem?.consumables
                ?.map((consumable) => ({
                    ...consumable,
                    specGroups: consumable?.specGroups
                        ?.map((specGroup) => {
                            const matchedItems = [];
                            const remainingItems = [];
                            for (const item of specGroup?.items || []) {
                                const stockClass = item?.stockClass?.toUpperCase();
                                const lineItemType = item?.lineItemType?.toUpperCase();
                                const description = item?.itemDescription?.toUpperCase();
                                const isFuel = stockClass === STOCK_CLASS_FUEL;
                                const isForced = item?.forceItem === FORCE_ITEM.YES;
                                const isMisc = lineItemType === STOCK_CLASS_MISCELLANEOUS;

                                if (isFuel && isForced && isMisc && description?.includes('FUEL')) {
                                    matchedItems.push(item);
                                } else {
                                    if (isFuel && isForced && isMisc && !description?.includes('FUEL')) {
                                        validFuelForcedItems.push(item);
                                    }
                                    remainingItems.push(item);
                                }
                            }
    
                            forcedFuelItems.push(...matchedItems);
    
                            return {
                                ...specGroup,
                                items: remainingItems,
                            };
                        })
                        ?.filter((specGroup) => specGroup?.items?.length > 0),
                }))
                ?.filter((consumable) => consumable?.specGroups?.length > 0),
        }));
    
        return {
            updatedResponse,
            forcedFuelItems,
            validFuelForcedItems,
        };
    };
    
    const getConsumables = async (pc = '', cartItems = cart?.availableCartItems) => {
        try {
            let data = consumables?.dataWithoutPrice;
            if (!data) {
                const items = cartItems?.map(item => item.product.sku);
                const response = await fetchConsumablesWithCache({
                    pcId: handlePayloadPC(pc),
                    account: handlePayloadAccount(),
                    jobNumber: handlePayloadJobNumber(),
                    items
                });
                if (!response) {
                    dispatch({ type: SET_CONSUMABLES, consumables: [] });
                    return;
                }
                const { updatedResponse, forcedFuelItems, validFuelForcedItems } = await excludeForcedFuelItems(response);
                data = updatedResponse;
                sessionStorage.setItem('validFuelForcedItems', JSON.stringify(validFuelForcedItems));
            }
            const skipConsumablesEstimates = isValidString(
                sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.SKIP_CONSUMABLES_ESTIMATES) || false
            );
            let estimates;
            if (skipConsumablesEstimates) {
                estimates = consumables?.rentablesWithPrice;
                sessionStorage.removeItem(STORAGE_CONFIG.SESSION_STORAGE.SKIP_CONSUMABLES_ESTIMATES);
            } else {
                estimates = await setRentablesPrice(data, pc);
            }
            dispatch({
                type: SET_CONSUMABLES,
                consumables: removeZeroPriceItems(data, estimates)
            });
            return { consumablesData: data, rentablesPriceObj: estimates };
        } catch (error) {
            if (error?.response?.status >= 500) {
                logError(error, true, 'getConsumables', [pc]);
            } else {
                logError(error, false, 'getConsumables', [pc]);
            }
        }
    };

    const formatAccessories = accessories => {
        try {
            const accessoriesObj = {};
            accessories?.forEach(accessory => {
                accessoriesObj[accessory?.sku] = {
                    name: accessory?.name,
                    qty: 1,
                    itemObj: {
                        forceItem: accessory?.forceItem
                    }
                };
            });
            return accessoriesObj;
        } catch (error) {
            logError(error, false, 'formatAccessories');
            return {};
        }
    };

    const formatAddons = addons => {
        try {
            const addonObj = {};
            addons?.forEach(addon => {
                const key = `${addon?.itemNumber}${addon?.stockClass}${addon?.oemStockNumber}`;
                addonObj[key] = {
                    name: addon?.name,
                    price: parseFloat(addon?.sellingPrice),
                    qty: 1,
                    itemObj: {
                        forceItem: addon?.forceItem,
                        oemStockNumber: addon?.oemStockNumber,
                        itemNumber: addon?.itemNumber,
                        lineItemType: addon?.lineItemType,
                        stockClass: addon?.stockClass,
                        sequence: addon?.sequence
                    }
                };
            });
            return addonObj;
        } catch (error) {
            logError(error, false, 'formatAddons');
            return {};
        }
    };

    const getConsumablesForAddToCart = (forcedItems, catsku) => {
        try {
            const forcedAccessories = forcedItems?.rentals;
            const forcedAddOn = forcedItems?.addons;
            if (!forcedAccessories?.length && !forcedAddOn?.length) {
                return '';
            }
            const { catId, classId } = extractCatAndClass(catsku);
            return JSON.stringify([
                {
                    cat: catId,
                    class: classId,
                    rentals: formatAccessories(forcedAccessories),
                    addons: formatAddons(forcedAddOn)
                }
            ]);
        } catch (error) {
            logError(error, false, 'getConsumablesForAddToCart');
        }
    };

    const updateAvailableQty = availableConsumableData => {
        dispatch({
            type: SET_SAVED_AVAILABLE_QUANTITY,
            key: 'savedAvailableQuantity',
            value: availableConsumableData
        });
        dispatch({
            type: SET_SAVED_AVAILABLE_QUANTITY,
            key: 'availableQuantity',
            value: availableConsumableData
        });
    };

    const getSelectedConsumablesAvailability = async (items, pc, enableLoader = false, stringifiedConsumables = '') => {
        try {
            enableLoader && dispatch({ type: SET_IS_CART_LOADING, isCartLoading: true });
            const selectedConsumables = isObjectEmpty(consumables?.selected)
                ? parseConsumablesData(stringifiedConsumables)
                : consumables?.selected;
            let updatedSelectedConsumables = structuredClone(selectedConsumables || {});
            let consumablesData, rentablesPriceObj;
            if (items?.length && isRentalDetailsAvailable()) {
                const getConsumablesData = await getConsumables(pc, items);
                ({ consumablesData, rentablesPriceObj } = getConsumablesData || {});
                if (consumablesData) {
                    const availableConsumableData = getAvailableConsumablesConfig(consumablesData);
                    updateSelectedConsumableData(
                        updatedSelectedConsumables,
                        consumablesData,
                        availableConsumableData,
                        items
                    );
                    updateAvailableQty(availableConsumableData);
                } else {
                    updatedSelectedConsumables = {};
                }
            }
            dispatch({
                type: SET_CONSUMABLES_SELECTED_ITEMS,
                selectedItems: updatedSelectedConsumables
            });
            enableLoader && dispatch({ type: SET_IS_CART_LOADING, isCartLoading: false });
            return { updatedSelectedConsumables, rentablesPriceObj };
        } catch (error) {
            logError(error, false, 'getSelectedConsumablesAvailability', [items]);
            enableLoader && dispatch({ type: SET_IS_CART_LOADING, isCartLoading: false });
            return {};
        }
    };

    const updateSelectedConsumableData = (
        selectedConsumables,
        consumablesData,
        availableConsumableData,
        availableProducts
    ) => {
        try {
            const availableProductsLookup = createAvailableProductsLooksup(availableProducts);
            removeForcedItemsFromSelected(selectedConsumables);
            const optionalItemsLookup = updateForcedAndGetOptionalItemsLookup(
                consumablesData,
                selectedConsumables,
                availableProductsLookup
            );
            updateOptionalSelectedItems(selectedConsumables, availableProductsLookup, optionalItemsLookup);
            updateSelectedAndAvailableQty(selectedConsumables, optionalItemsLookup, availableConsumableData);
        } catch (error) {
            logError(error, false, 'updateSelectedConsumableData', [selectedConsumables, consumablesData]);
            return {};
        }
    };

    const createAvailableProductsLooksup = availableProducts => {
        try {
            const lookup = {};
            availableProducts?.forEach(availableProduct => {
                lookup[availableProduct?.product?.sku] = availableProduct;
            });
            return lookup;
        } catch (error) {
            logError(error, false, 'createAvailableProductsLooksup', availableProducts);
            return {};
        }
    };

    const removeForcedItemsFromSelected = selectedConsumables => {
        try {
            Object.keys(selectedConsumables).forEach(key => {
                const addons = selectedConsumables?.[key]?.addons || {};
                const accessories = selectedConsumables?.[key]?.rentals || {};
                Object.keys(addons)?.forEach(addonKey => {
                    if (addons[addonKey]?.itemObj?.forceItem === FORCE_ITEM.YES) {
                        delete addons[addonKey];
                    }
                });
                Object.keys(accessories)?.forEach(accessorySku => {
                    if (accessories[accessorySku]?.itemObj?.forceItem === FORCE_ITEM.YES) {
                        delete accessories[accessorySku];
                    }
                });
                // after removing forced items, if the product do not have any selected consumables
                // then removing the data from selectedConsumables
                if (isObjectEmpty(addons) && isObjectEmpty(accessories)) {
                    delete selectedConsumables[key];
                }
            });
        } catch (error) {
            logError(error, false, 'removeForcedItemsFromSelected', [selectedConsumables]);
        }
    };

    /**
     * This function iterates over consumablesData, adding forced items in selectedConsumables and creating a lookup for optional items
     * @param {*} consumablesData 
     * @param {*} selectedConsumables 
     * @param {*} availableProductsLookup used to update the qty of forced item based on the main product qty
     * @returns lookup for optional items
     */
    const updateForcedAndGetOptionalItemsLookup = (consumablesData, selectedConsumables, availableProductsLookup) => {
        try {
            const lookup = {};
            consumablesData?.forEach(consumable => {
                const productSku = generateSKU(consumable?.cat, consumable?.class);
                const forcedAccessories = {};
                const forcedAddOns = {};
                let isForcedItemPresent = false;

                consumable?.rentables?.forEach(accessory => {
                    const accessorySku = accessory?.product?.sku;
                    const itemObj = {
                        forceItem: accessory?.forceItem,
                        sequence: accessory?.sequence,
                        minRate: accessory?.minRate,
                        dayRate: accessory?.dayRate,
                        wkRate: accessory?.wkRate,
                        moRate: accessory?.moRate,
                        IsSerialized: accessory?.IsSerialized,
                        category_name: accessory?.product?.category_name,
                        superCategory: accessory?.product?.categories?.[0]?.name,
                        product_page_url: accessory?.product?.product_page_url,
                        equipmentNumber: accessory?.equipmentNumber
                    };
                    if (accessory?.forceItem === FORCE_ITEM.YES) {
                        isForcedItemPresent = true;
                        forcedAccessories[accessorySku] = {
                            name: accessory?.description,
                            qty: availableProductsLookup?.[productSku]?.quantity,
                            isAvailable: true,
                            itemObj
                        };
                    } else {
                        lookup[accessorySku] = { itemObj };
                    }
                });

                consumable?.consumables?.forEach(addon => {
                    addon?.specGroups?.forEach(specGroup => {
                        specGroup?.items?.forEach(item => {
                            const addonKey = `${item?.itemNumber}${item?.stockClass}${item?.oemStockNumber}`;
                            if (item?.forceItem === FORCE_ITEM.YES) {
                                isForcedItemPresent = true;
                                forcedAddOns[addonKey] = {
                                    name: item?.itemDescription,
                                    price: parseFloat(item?.sellingPrice),
                                    qty: availableProductsLookup?.[productSku]?.quantity,
                                    itemObj: {
                                        forceItem: item?.forceItem,
                                        oemStockNumber: item?.oemStockNumber,
                                        itemNumber: item?.itemNumber,
                                        lineItemType: item?.lineItemType,
                                        stockClass: item?.stockClass,
                                        sequence: item?.sequence,
                                        sellingPrice: item?.sellingPrice
                                    },
                                    isAvailable: true
                                };
                            } else {
                                lookup[addonKey] = {
                                    price: parseFloat(item?.sellingPrice)
                                };
                            }
                        });
                    });
                });

                if (isForcedItemPresent) {
                    const productAccessories = selectedConsumables?.[productSku]?.rentals || {};
                    const productAddons = selectedConsumables?.[productSku]?.addons || {};
                    selectedConsumables[productSku] = {
                        rentals: {
                            ...productAccessories,
                            ...forcedAccessories
                        },
                        addons: {
                            ...productAddons,
                            ...forcedAddOns
                        }
                    };
                }
            });
            return lookup;
        } catch (error) {
            logError(error, false, 'createConsumablesLookup', [consumablesData]);
            return {};
        }
    };

    const updateOptionalSelectedItems = (selectedConsumables, availableProductsLookup, optionalItemsLookup) => {
        try {
            Object.keys(selectedConsumables).forEach(key => {
                const addons = selectedConsumables?.[key]?.addons || {};
                const accessories = selectedConsumables?.[key]?.rentals || {};
                updateSelectedDetails(addons, optionalItemsLookup, true, !!availableProductsLookup?.[key]);
                updateSelectedDetails(accessories, optionalItemsLookup, false, !!availableProductsLookup?.[key]);
            });
        } catch (error) {
            logError(error, false, 'updateOptionalSelectedItems');
        }
    };

    const updateSelectedDetails = (items, lookup, isAddon, isMainProductAvailable) => {
        try {
            Object.keys(items)?.forEach(itemKey => {
                const itemVal = items[itemKey];
                if (itemVal?.itemObj?.forceItem === FORCE_ITEM.YES) {
                    // not updating details for forced items as it is already done
                    return;
                }
                // item is available if main product is available and that item is present in the consumabled data
                itemVal.isAvailable = isMainProductAvailable && !!lookup?.[itemKey];
                if (isAddon) {
                    itemVal.price = lookup?.[itemKey]?.price || itemVal?.price;
                } else {
                    itemVal.itemObj = lookup?.[itemKey]?.itemObj || {};
                }
                if (itemVal.isAvailable && lookup?.[itemKey]) {
                    // checking only for available items
                    if (!lookup?.[itemKey]?.addedQty) {
                        lookup[itemKey]['addedQty'] = itemVal.qty;
                        lookup[itemKey]['isAddedMoreThanOnce'] = false;
                    } else {
                        lookup[itemKey]['addedQty'] += itemVal.qty;
                        lookup[itemKey]['isAddedMoreThanOnce'] = true;
                    }
                }
            });
        } catch (error) {
            logError(error, false, 'updateSelectedDetails', [items, lookup]);
        }
    };

    const updateSelectedAndAvailableQty = (selectedConsumables, optionalItemsLookup, availableConsumableData) => {
        try {
            Object.keys(selectedConsumables)?.forEach(key => {
                const addons = selectedConsumables?.[key]?.addons || {};
                const accessories = selectedConsumables?.[key]?.rentals || {};
                updateQtyForConsumable(addons, optionalItemsLookup, availableConsumableData, true);
                updateQtyForConsumable(accessories, optionalItemsLookup, availableConsumableData, false);
            });
        } catch (error) {
            logError(error, false, 'updateSelectedAndAvailableQty');
        }
    };

    const updateQtyForConsumable = (consumables, optionalItemsLookup, availableConsumableData, isAddon) => {
        try {
            const remainingKey = isAddon ? 'remainingAddOns' : 'remainingAccessories';
            const availableQtyKey = isAddon ? 'addOnsAvailableQuantity' : 'accessoriesAvailableQuantity';
            const remainingQtyKey = isAddon ? 'addOnsRemainingQuantity' : 'accessoriesRemainingQuantity';
            Object.keys(consumables)?.forEach(key => {
                const itemVal = consumables?.[key] || {};
                if (itemVal?.itemObj?.forceItem === FORCE_ITEM.YES) {
                    // not updating for forced items
                    return;
                }
                if (
                    availableConsumableData?.[remainingKey]?.[key]?.[availableQtyKey] == null ||
                    availableConsumableData?.[remainingKey]?.[key]?.[remainingQtyKey] == null ||
                    optionalItemsLookup?.[key]?.addedQty == null
                ) {
                    // handling scenario where data is not present
                    return;
                }
                if (
                    optionalItemsLookup?.[key]?.addedQty >
                    availableConsumableData?.[remainingKey]?.[key]?.[availableQtyKey]
                ) {
                    // if added qty for the consumable is more than the available qty
                    if (optionalItemsLookup?.[key]?.isAddedMoreThanOnce) {
                        // if same consumable is added multiple times, then removing the consumable from selected
                        // removing even if it is unavailable
                        delete consumables[key];
                    } else if (itemVal?.isAvailable) {
                        // updating the added qty and remaining qty if consumable is only added once
                        // updating only for available items
                        consumables[key].qty = availableConsumableData?.[remainingKey]?.[key]?.[availableQtyKey];
                        availableConsumableData[remainingKey][key][remainingQtyKey] = 0;
                    }
                } else {
                    // updating remaining qty
                    availableConsumableData[remainingKey][key][remainingQtyKey] =
                        availableConsumableData?.[remainingKey]?.[key]?.[availableQtyKey] -
                        optionalItemsLookup?.[key]?.addedQty;
                }
            });
        } catch (error) {
            logError(error, false, 'updateQtyForConsumable');
        }
    }

    const removeUnavailableConsumables = consumables => {
        try {
            if (!consumables) return;
            const keys = Object.keys(consumables);
            for (let key of keys) {
                if (!consumables[key]?.isAvailable) {
                    delete consumables[key];
                }
            }
        } catch (error) {
            logError(error, false, 'removeUnavailableConsumables', [consumables]);
        }
    };

    /**
     * This function will format the stringified data coming from cart details api
     * @param consumables - stringified data from cart details api
     * @returns formated selected items
     */
    const parseConsumablesData = (consumables, removeUnavailable = false) => {
        try {
            if (!consumables) return {};
            const selectedConsumables = {};
            const productsWithConsumables = JSON.parse(consumables);
            productsWithConsumables?.forEach(product => {
                const { cat, class: classId, addons, rentals } = product || {};
                if (removeUnavailable) {
                    removeUnavailableConsumables(addons);
                    removeUnavailableConsumables(rentals);
                }
                if (isObjectWithKeys(addons || {}) || isObjectWithKeys(rentals || {})) {
                    const sku = generateSKU(cat, classId);
                    selectedConsumables[sku] = { addons, rentals };
                }
            });
            return selectedConsumables;
        } catch (error) {
            logError(error, false, 'parseConsumablesData', [consumables]);
            return {};
        }
    };

    const stringifyConsumablesData = consumables => {
        try {
            const formattedConsumables = Object.keys(consumables)?.map(sku => {
                const { catId, classId } = extractCatAndClass(sku);
                return {
                    ...(consumables[sku] || {}),
                    cat: catId,
                    class: classId
                };
            });
            return JSON.stringify(formattedConsumables);
        } catch (error) {
            logError(error, false, 'stringifyConsumablesData', [consumables]);
            return '';
        }
    };

    const updateItemConsumables = async (sku, selectedItems) => {
        try {
            dispatch({ type: SET_IS_CART_LOADING, isCartLoading: true });
            const response = await updateConsumablesAction({
                updateConsumablesGQL,
                cartId,
                consumables: stringifyConsumablesData({ [sku]: selectedItems })
            });
            if (response?.updateCartAccessories?.message !== SUCCESS?.toUpperCase()) {
                return false;
            }
            const updatedSelectedItems = {
                ...consumables?.selected,
                [sku]: selectedItems
            };
            dispatch({
                type: SET_CONSUMABLES_SELECTED_ITEMS,
                selectedItems: updatedSelectedItems
            });
            dispatch({ type: SET_IS_CART_LOADING, isCartLoading: false });
            return true;
        } catch (error) {
            logError(error, false, 'updateItemConsumables', [sku, selectedItems]);
            dispatch({ type: SET_IS_CART_LOADING, isCartLoading: false });
            return false;
        }
    };

    const removeItemsConsumables = (productSkus, updateSelectedItems = false) => {
        try {
            const updatedSelectedItems = { ...(consumables?.selected || {}) };
            if (isObjectEmpty(updatedSelectedItems)) return;
            productSkus?.forEach(sku => delete updatedSelectedItems[sku]);
            if (updateSelectedItems) {
                dispatch({
                    type: SET_CONSUMABLES_SELECTED_ITEMS,
                    selectedItems: updatedSelectedItems
                });
            }
            return updatedSelectedItems;
        } catch (error) {
            logError(error, false, 'removeItemsConsumables', [productSkus, updateSelectedItems]);
            return {};
        }
    };

    const bulkUpdateConsumables = async (selectedConsumables = consumables?.selected, availableItems = []) => {
        try {
            // enhancement - not calling if no available items are present or cap details are not filled
            // as user cannot interact with consumables for these scenarios
            if (cartId && isRentalDetailsAvailable() && availableItems?.length > 0) {
                const response = await updateConsumablesAction({
                    updateConsumablesGQL: bulkUpdateConsumablesGQL,
                    cartId,
                    consumables: stringifyConsumablesData(selectedConsumables)
                });
                return response?.bulkupdateCartAccessories?.message === SUCCESS?.toUpperCase();
            }
            return true;
        } catch (error) {
            logError(error, false, 'bulkUpdateConsumables', [selectedConsumables]);
            return '';
        }
    };

    const parseAndSaveSelectedConsumables = stringifiedConsumables => {
        try {
            if (!stringifiedConsumables) return;
            const selectedConsumables = parseConsumablesData(stringifiedConsumables, true);
            dispatch({
                type: SET_CONSUMABLES_SELECTED_ITEMS,
                selectedItems: selectedConsumables
            });
        } catch (error) {
            logError(error, false, 'parseAndSaveSelectedConsumables', [stringifiedConsumables]);
        }
    };

    const updateSelectedRentalQtyAndPrice = accessoryPriceObj => {
        try {
            const selectedRentalQtyAndPrice = getTotalFromConsumables(consumables?.selected || {}, accessoryPriceObj);
            dispatch({
                type: UPDATE_CONSUMABLES,
                key: 'orderSummaryRentals',
                value: consumables?.selected
            });
            dispatch({
                type: UPDATE_CONSUMABLES,
                key: 'selectedRentalQtyAndPrice',
                value: selectedRentalQtyAndPrice
            });
        } catch (error) {
            logError(error, false, 'updateSelectedRentalQtyAndPrice', [accessoryPriceObj]);
        }
    };

    /** call consumables api and finds all the unavailable consumables */
    const getUnavailableConsumablesOnCapChange = async (items, pcId) => {
        try {
            if (isObjectEmpty(consumables?.selected)) return {};
            const itemsSku = items?.map(item => item?.product?.sku);
            const response = await fetchConsumablesWithCache({
                pcId,
                account: handlePayloadAccount(),
                jobNumber: handlePayloadJobNumber(),
                items: itemsSku
            })
            const { updatedResponse: consumablesData } = await excludeForcedFuelItems(response);
            const unavailableConsumables = getUnavailableConsumables(consumablesData, itemsSku);
            return { consumablesData, unavailableConsumables };
        } catch (error) {
            if (error?.response?.status >= 500) {
                logError(error, true, 'getUnavailableConsumablesOnCapChange', [pcId]);
            } else {
                logError(error, false, 'getUnavailableConsumablesOnCapChange', [pcId]);
            }
            return {};
        }
    };

    const createConsumablesLookupForQty = consumablesData => {
        try {
            const lookup = {};
            consumablesData?.forEach(consumable => {
                consumable?.rentables?.forEach(accessory => {
                    const accessorySku = accessory?.product?.sku;
                    lookup[accessorySku] = {
                        availQuantity: parseInt(accessory?.availQuantity || 0)
                    };
                });

                consumable?.consumables?.forEach(addon => {
                    addon?.specGroups?.forEach(specGroup => {
                        specGroup?.items?.forEach(item => {
                            const addonKey = `${item?.itemNumber}${item?.stockClass}${item?.oemStockNumber}`;
                            lookup[addonKey] = {
                                availQuantity: parseInt(item?.availQuantity || 0)
                            };
                        });
                    });
                });
            });
            return lookup;
        } catch (error) {
            logError(error, false, 'createConsumablesLookupForQty', [consumablesData]);
            return {};
        }
    }

    const createSelectedConsumablesLookup = mainProductsSku => {
        try {
            const lookup = {}
            mainProductsSku?.forEach(productSku => {
                const accessories = consumables?.selected?.[productSku]?.rentals || {};
                const addons = consumables?.selected?.[productSku]?.addons || {};
                Object.keys(accessories)?.forEach(accessorySku => {
                    if (lookup[accessorySku]) {
                        lookup[accessorySku] = {
                            isAddedMoreThanOnce: true
                        }
                    } else {
                        lookup[accessorySku] = {
                            addedQty: accessories?.[accessorySku]?.qty,
                            isAddedMoreThanOnce: false
                        }
                    }
                });
                Object.keys(addons)?.forEach(addonKey => {
                    if (lookup[addonKey]) {
                        lookup[addonKey] = {
                            isAddedMoreThanOnce: true
                        }
                    } else {
                        lookup[addonKey] = {
                            addedQty: addons?.[addonKey]?.qty,
                            isAddedMoreThanOnce: false
                        }
                    }
                });
            });
            return lookup;
        } catch (error) {
            logError(error, false, 'createSelectedConsumablesLookup');
            return {};
        }
    }

    const getUnavailableConsumables = (consumablesData, itemsSku) => {
        try {
            const unavailableAccessoriesMap = {};
            const unavailableAddonsMap = {};
            const lookup = createConsumablesLookupForQty(consumablesData);
            const selectedConsumablesLookup = createSelectedConsumablesLookup(itemsSku);
            itemsSku?.forEach(itemSku => {
                const selectedConsumables = consumables?.selected?.[itemSku];
                if (selectedConsumables) {
                    const addons = selectedConsumables?.addons || {};
                    const accessories = selectedConsumables?.rentals || {};
                    updateUnavailableData(addons, lookup, selectedConsumablesLookup, unavailableAddonsMap, false);
                    updateUnavailableData(accessories, lookup, selectedConsumablesLookup, unavailableAccessoriesMap, true);
                }
            });
            return {
                unavailableAccessories: Object.values(unavailableAccessoriesMap),
                unavailableAddons: Object.values(unavailableAddonsMap)
            };
        } catch (error) {
            logError(error, false, 'getUnavailableConsumables', [consumablesData]);
            return {};
        }
    };

    /** find and add unavailable items in unavailableMap */
    const updateUnavailableData = (items, availableDataLookup, addedDataLookup, unavailableMap, isAccessory) => {
        try {
            Object.keys(items)?.forEach(itemKey => {
                const data = items?.[itemKey] || {};
                if (data?.itemObj?.forceItem === FORCE_ITEM.YES) return;

                if (!availableDataLookup[itemKey]) {
                    // if item is unavailable
                    unavailableMap[itemKey] = {
                        ...data,
                        isAvailable: false,
                        sku: isAccessory ? itemKey : ''
                    };
                } else if (
                    availableDataLookup?.[itemKey]?.availQuantity < addedDataLookup?.[itemKey]?.addedQty &&
                    !availableDataLookup?.[itemKey]?.isAddedMoreThanOnce &&
                    !unavailableMap[itemKey]
                ) {
                    // if item available qty is less than the total added qty
                    // and the item should not be added with multiple products
                    // adding it in map, if not added already
                    unavailableMap[itemKey] = {
                        ...data,
                        isAvailable: true,
                        isQtyGtrThanAvailable: true,
                        maxQty: availableDataLookup?.[itemKey]?.availQuantity,
                        sku: isAccessory ? itemKey : ''
                    };
                }
            });
        } catch (error) {
            logError(error, false, 'updateUnavailableData', [items, lookup, unavailableMap]);
            return {};
        }
    };

    /** this function removes unavailable items consumables for override pc scenario */
    const removeUnavailableItemsConsumables = (unavailableItems, stringifiedConsumables) => {
        try {
            if (!stringifiedConsumables) return '';
            const parsedConsumables = parseConsumablesData(stringifiedConsumables);
            unavailableItems?.forEach(item => {
                delete parsedConsumables[item?.product?.sku];
            });
            return stringifyConsumablesData(parsedConsumables);
        } catch (error) {
            logError(error, false, 'removeUnavailableItemsConsumables', [unavailableItems, stringifiedConsumables]);
        }
    };
 
    const isOptionalItemNotSelected = availableItems => {
        const selectedConsumables = consumables?.selected || {};
        const availableItemsLookup = {};
        availableItems.forEach(item => {
            const sku = item?.product?.sku;
            if (!availableItemsLookup[sku]) {
                availableItemsLookup[sku] = true;
            }
        });

        for (let consumableSku in selectedConsumables) {
            if (Object.hasOwn(selectedConsumables, consumableSku)) {
                if (!availableItemsLookup[consumableSku]) {
                    continue;
                }

                const accessories = selectedConsumables[consumableSku]?.rentals || {};
                const addons = selectedConsumables[consumableSku]?.addons || {};

                for (let key in accessories) {
                    if (accessories[key]?.itemObj?.forceItem !== FORCE_ITEM.YES) {
                        return false;
                    }
                }

                for (let key in addons) {
                    if (addons[key]?.itemObj?.forceItem !== FORCE_ITEM.YES) {
                        return false;
                    }
                }
            }
        }
        return true;
    };

    const isRentalsOrAddonsSelected = () => {
        const selectedConsumables = consumables?.selected || {};

        for (let consumableSku in selectedConsumables) {
            if (Object.hasOwn(selectedConsumables, consumableSku)) {
                const accessories = selectedConsumables[consumableSku]?.rentals || {};
                const addons = selectedConsumables[consumableSku]?.addons || {};

                for (let key in accessories) {
                    if (Object.hasOwn(accessories, key)) {
                        return true;
                    }
                }

                for (let key in addons) {
                    if (Object.hasOwn(addons, key)) {
                        return true;
                    }
                }
            }
        }

        return false;
    };

    const isOptionalItemsAvailable = () => {
        const consumablesData = consumables?.data || [];
        for (let data of consumablesData) {
            const accessories = data?.rentables || [];
            const addons = data?.consumables || [];

            for (let accessory of accessories) {
                if (accessory?.forceItem !== FORCE_ITEM.YES) {
                    return true;
                }
            }

            for (let addon of addons) {
                const specGroups = addon?.specGroups || [];
                for (let specGroup of specGroups) {
                    const items = specGroup?.items || [];
                    for (let item of items) {
                        if (item?.forceItem !== FORCE_ITEM.YES) {
                            return true;
                        }
                    }
                }
            }
        }
        return false;
    };

    const isAvailableOptionalItemsNotSelected = availableItems => {
        try {
            if (!isOptionalItemsAvailable()) {
                return false;
            }
            return isOptionalItemNotSelected(availableItems);
        } catch (error) {
            logError(error, false, 'isAvailableOptionalItemsNotSelected');
            return false;
        }
    };

    const api = {
        getConsumablesForAddToCart,
        getSelectedConsumablesAvailability,
        updateItemConsumables,
        removeItemsConsumables,
        bulkUpdateConsumables,
        updateSelectedRentalQtyAndPrice,
        parseAndSaveSelectedConsumables,
        getUnavailableConsumablesOnCapChange,
        removeUnavailableItemsConsumables,
        isAvailableOptionalItemsNotSelected,
        isRentalsOrAddonsSelected
    };

    return [api];
};

export default useConsumables;
